import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Switch,
  useParams,
  Route,
  Redirect,
} from 'react-router-dom';
import moment from 'moment';

import Progress from '../../components/Core/Progress';
import Logbook from '../../components/Logbook/Logbook';
import { getCompanyInfo, getOrganisationModules } from '../../store/company/actions';
import { loadLogbookAdditionalRates } from '../../store/settings/actions';
import { companyModulesLoading, isLoadingCompanySelector } from '../../store/company/selectors';
import { userSelector } from '../../store/auth/selectors';

import Events from '../../components/Events/Events';
import ScheduleV2 from '../ScheduleV2';
import Places from '../Places';
import TimeOffScreen from '../TimeOff'
import SimpleSchedule from '../SimpleSchedule';
import CreateShift from '../Schedule/Shift';
import TimeSheet from '../TimeSheet';
import Invoices from '../Invoices'
import Assets from '../Assets'
import Documents from '../Documents'
import Settings from '../../components/Settings';
import SettingCompany from '../../components/Settings/General/Company';
import SettingWorkTime from '../../components/Settings/General/WorkTime';
import SettingSecurity from '../../components/Settings/General/Security';
import Accounts from '../../components/Settings/Accounts/AccountsList';
import Grouping from '../../components/Settings/Accounts/Grouping';
import Roles from '../../components/Settings/Accounts/Roles';
import ScheduleSettings from '../../components/Settings/Schedule';
import SimpleScheduleSettings from '../../components/Settings/SimpleSchedule';
import KioskList from '../../components/Settings/Kiosk/KioskList';
import KioskUsers from '../../components/Settings/Kiosk/KioskUsers';
import SettingCategories from '../../components/Settings/Categories';
import CustomerForm from '../../components/Settings/CustomerForm';
import SettingJournal from '../../components/Settings/Logbook/Journal';
import Overtime from '../../components/Settings/Logbook/Overtime';
import AdditionalRates from '../../components/Settings/Logbook/AdditionalRates';
import InvoicesSettings from '../../components/Settings/Invoices';
import Clock from '../../components/Settings/Logbook/Clock';
import ActivityLog from '../../components/Settings/ActivityLog';
import TimeOff from '../../components/Settings/TimeOff';
import TimeSheetSettings from '../../components/Settings/TimeSheet';
import IntegrationsSettings from '../../components/Settings/Integrations';
import SettingDelete from '../../components/Settings/Delete/DataDelete';
import AutoDelete from '../../components/Settings/Delete/AutoDelete';
import SettingEvents from '../../components/Settings/Events';
import Reports from '../../components/Reports/Reports';
import usePermissions from '../../components/Core/usePermissions';
import DocumentTypes from '../Settings/Documents/DocumentTypes'
import { NotificationContainer, addNotification } from "../../components/Core/NotificationContainer/NotificationContainer";
import pusher from '../../pusher';
import getToken from '../../store/getToken';

import styles from './Company.module.scss';
import { uniqueId } from 'lodash';

const permissionsConfig = [
  {
    name: 'company_edit_settings',
    permission: 'company_edit_settings',
  },
  {
    name: 'logbook',
    module: 'logbook',
    permission: 'logbook_module_access',
  },
  {
    name: 'places',
    module: 'places',
  },
  {
    name: 'logbook_edit_settings',
    permission: 'logbook_edit_settings',
  },
  {
    name: 'additional_rates',
    permission: 'additional_rates',
  },
  {
    name: 'activity_log',
    permission: 'activity_log_view',
    module: 'activity_log',
  },
  {
    name: 'groups',
    module: 'create_groups',
    permission: 'groups_create',
  },
  {
    name: 'events',
    module: 'events',
    permission: 'events_module_access',
  },
  {
    name: 'events_create',
    permission: 'events_create',
  },
  {
    name: 'roles_create',
    permission: 'roles_create',
  },
  {
    name: 'data_delete',
    permission: 'data_delete',
  },
  {
    name: 'categories_create',
    permission: 'categories_create',
  },
  {
    name: 'accounts_see_and_edit',
    permission: 'accounts_see_and_edit',
  },
  {
    name: 'kiosk',
    permission: 'kiosk_create',
    module: 'kiosk',
  },
  {
    name: 'schedule_shift_access',
    module: 'schedule_shift',
    permission: 'schedule_module_access',
  },
  {
    name: 'schedule_simple_access',
    module: 'schedule_simple',
    permission: 'schedule_module_access',
  },
  {
    name: 'schedule_shift',
    module: 'schedule_shift',
    permission: 'schedule_create_and_edit',
  },
  {
    name: 'reports',
    module: 'reports',
    permission: 'reports_module_access',
  },
  {
    name: 'time_sheet_edit_settings',
    module: 'time_sheet',
    permission: 'time_sheet_edit_settings',
  },
  {
    name: 'time_sheet_module',
    module: 'time_sheet',
    permission: 'time_sheet_module_access',
  },
  {
    name: 'integrations_edit_settings',
    module: 'integrations',
    permission: 'integrations_edit_settings',
  },
  {
    name: 'time_off',
    module: 'time_off',
  },

  {
    name: 'schedule_edit',
    permission: 'schedule_edit',
  },
  {
    name: 'schedule_edit_settings',
    permission: 'schedule_edit_settings',
  },
  {
    name: 'schedule_shift',
    module: 'schedule_shift',
    permission: 'schedule_module_access',
  },
  {
    name: 'schedule_simple',
    module: 'schedule_simple',
    permission: 'schedule_module_access',
  },
  {
    name: 'time_off_edit_settings',
    module: 'time_off',
    permission: 'time_off_edit_settings',
  }, {
    name: 'invoices',
    module: 'invoices',
    permission: 'invoices_module_access',
  }, {
    name: 'assets',
    module: 'assets',
  }, {
    name: 'documents',
    module: 'documents',
  }
];

const parseJwt = (token) => {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  )
  return JSON.parse(jsonPayload)
}


export default () => {
  const { id: companyId } = useParams();
  const dispatch = useDispatch();
  const permissions = usePermissions(permissionsConfig);

  const isLoading = useSelector(isLoadingCompanySelector);
  const isLoadingCompanyModules = useSelector(companyModulesLoading);
  const user = useSelector(userSelector);

  useEffect(() => {
    if (companyId) {
      dispatch(getCompanyInfo(companyId, true))
      dispatch(getOrganisationModules(companyId))
      dispatch(loadLogbookAdditionalRates(companyId))
    }
    // eslint-disable-next-line
  }, [companyId]);

  useEffect(() => {
    if (user) {
      const token = localStorage.getItem('token')
      if (token) {
        const parsedToken = parseJwt(token)
        const now = moment()
        console.log('expire: ', moment.unix(parsedToken.exp).fromNow())
        if (parsedToken.exp && now.isAfter(moment.unix(parsedToken.exp))) {
          localStorage.setItem('token', '')
          localStorage.setItem('user', '')
          window.location.href = '/'
        }
      }
    }
  }, [user])

  useEffect(() => {
    if (user?.user?.id) {
      pusher.init(getToken(), (status) => {
        if (status.type === 'connected') {
          pusher.openUserChannel(user?.user?.id)
          setTimeout(() => {
            pusher.listenUserNotify(data => {
              console.log('data from pusher', data)
              if (data.type === 'ai_generation_changes') {
                dispatch({type: 'SET_AI_EVENT_GENERATION', payload: {
                  ...data,
                  uniqueId: uniqueId('ai_event_generation_')
                }})
                return
              }
              if (data.type === 'ai_time_sheet_generation_changes') {
                dispatch({type: 'SET_AI_EVENT_GENERATION', payload: {
                  ...data,
                  uniqueId: uniqueId('ai_event_generation_')
                }})
                return
              }
              addNotification(data)
            });
          }, 500);
        }
      })
    }
  }, [user?.user?.id])

  const isSuperAdmin = user?.user?.role_id === 1;
  const isCompanyOwner = user?.user?.role_id === 2 && (user?.user?.owned_companies_ids ?? []).includes(Number(companyId));
  if (!isSuperAdmin && !isCompanyOwner && user.employee.company_id !== +companyId) {
    return (
      <Redirect from='*' to='/404' />
    );
  }

  if (isLoading || isLoadingCompanyModules) {
    return (
      <div className={styles.progressBlock}>
        <Progress />
      </div>
    );
  }

  return (
    <>
      <NotificationContainer />
      <Switch>
        {
          permissions.logbook && (
            <Route exact path='/:id/logbook' component={Logbook} />
          )
        }
        {
          permissions.places && (
            <Route exact path='/:id/places' component={Places} />
          )
        }
        {
          permissions.events && (
            <Route exact path='/:id/events' component={Events} />
          )
        }
        {
          (permissions.time_sheet_module) && (
            <Route exact path='/:id/time-sheet' component={TimeSheet} />
          )
        }
        {/* {
          (permissions.schedule_shift_access || permissions.schedule_simple_access) && (
            <Route exact path='/:id/schedule' component={permissions.schedule_simple_access ? SimpleSchedule : Schedule} />
          )
        } */}
        {
          (permissions.schedule_shift_access || permissions.schedule_simple_access) && (
            <Route exact path='/:id/schedule' component={permissions.schedule_simple_access ? SimpleSchedule : ScheduleV2} />
          )
        }
        {
          (permissions.schedule_shift_access || permissions.schedule_simple_access) && (
            <Route exact path='/:id/schedule/shift/:shiftId' component={CreateShift} />
          )
        }
        {
          (permissions.schedule_shift_access || permissions.schedule_simple_access) && (
            <Route exact path='/:id/schedule/shift/create' component={CreateShift} />
          )
        }
        {
          permissions.reports && (
            <Route exact path='/:id/reports' component={Reports} />
          )
        }
        {
          permissions.assets && (
            <Route exact path='/:id/assets' component={Assets} />
          )
        }
        {
          permissions.documents && (
            <Route exact path='/:id/documents' component={Documents} />
          )
        }
        <Route exact path='/:id/settings' component={Settings} />
        {
          permissions.documents && (
            <Route exact path='/:id/settings/documents/document-types' component={DocumentTypes} />
          )
        }
        {
          permissions.company_edit_settings && (
            <Route exact path='/:id/settings/general/company' component={SettingCompany} />
          )
        }
        {
          permissions.company_edit_settings && (
            <Route exact path='/:id/settings/general/work-time' component={SettingWorkTime} />
          )
        }
        {
          permissions.company_edit_settings && (
            <Route exact path='/:id/settings/general/security' component={SettingSecurity} />
          )
        }
        {
          permissions.accounts_see_and_edit && (
            <Route exact path='/:id/settings/accounts/accounts-list' component={Accounts} />
          )
        }
        {
          permissions.groups && (
            <Route exact path='/:id/settings/accounts/grouping' component={Grouping} />
          )
        }
        {
          permissions.roles_create && (
            <Route exact path='/:id/settings/accounts/roles' component={Roles} />
          )
        }
        {
          ((permissions.schedule_shift && permissions.schedule_edit) || (permissions.schedule_simple && permissions.schedule_edit_settings)) && (
            <Route exact path='/:id/settings/schedule' component={permissions.schedule_simple_access ? SimpleScheduleSettings : ScheduleSettings} />
          )
        }
        {
          permissions.kiosk && (
            <Route exact path='/:id/settings/kiosk/kiosk-list' component={KioskList} />
          )
        }
        {
          permissions.kiosk && (
            <Route exact path='/:id/settings/kiosk/users' component={KioskUsers} />
          )
        }
        {
          permissions.categories_create && (
            <Route exact path='/:id/settings/categories' component={SettingCategories} />
          )
        }
        {
          permissions.categories_create && (
            <Route exact path='/:id/settings/categories/customers/:customerId' component={CustomerForm} />
          )
        }
        {
          (permissions.logbook && permissions.logbook_edit_settings) && (
            <Route exact path='/:id/settings/logbook/journal' component={SettingJournal} />
          )
        }
        {
          (permissions.logbook && permissions.logbook_edit_settings) && (
            <Route exact path='/:id/settings/logbook/overtime' component={Overtime} />
          )
        }
        {
          (permissions.logbook && permissions.logbook_edit_settings) && (
            <Route exact path='/:id/settings/logbook/additional-rates' component={AdditionalRates} />
          )
        }
        {
          (permissions.logbook && permissions.logbook_edit_settings) && (
            <Route exact path='/:id/settings/logbook/clock' component={Clock} />
          )
        }
        {
          permissions.activity_log && (
            <Route exact path='/:id/settings/activity-log' component={ActivityLog} />
          )
        }
        {
          permissions.time_off && permissions.time_off_edit_settings && (
            <Route exact path='/:id/settings/time-off' component={TimeOff} />
          )
        }
        {
           permissions.invoices && (
            <Route exact path='/:id/settings/invoices' component={InvoicesSettings} />
          )
        }
        {
          permissions.time_off && (
            <Route exact path='/:id/time-off' component={TimeOffScreen} />
          )
        }
        {
          permissions.invoices && (
            <Route exact path='/:id/invoices' component={Invoices} />
          )
        }
        {
          permissions.time_sheet_edit_settings && (
            <Route exact path='/:id/settings/time_sheet' component={TimeSheetSettings} />
          )
        }
        {
          permissions.integrations_edit_settings && (
            <Route exact path='/:id/settings/integrations' component={IntegrationsSettings} />
          )
        }
        {
          permissions.data_delete && (
            <Route exact path='/:id/settings/delete/data-delete' component={SettingDelete} />
          )
        }
        {
          permissions.data_delete && (
            <Route exact path='/:id/settings/delete/auto-delete' component={AutoDelete} />
          )
        }
        {
          (permissions.events && permissions.events_create) && (
            <Route exact path='/:id/settings/events' component={SettingEvents} />
          )
        }
        <Redirect from='*' to='/404' />
      </Switch>
    </>
  );
};